import SEO, { pageSEO } from "../components/seo.component";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalculator,
  faHouse,
  faDollarSign,
  faChartLine,
} from "@fortawesome/free-solid-svg-icons";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../functions/animations";


export default function Calculator() {

  const seoConfig = pageSEO.calculator();

  const cards = [
    {
      icon: faCalculator,
      title: "Estimate Payments",
      text: "Calculate your expected monthly mortgage payments and understand your affordability."
    },
    {
      icon: faDollarSign,
      title: "Plan Your Budget",
      text: "Explore different down payments, rates, and loan terms before making decisions."
    },
    {
      icon: faHouse,
      title: "Smart Home Buying",
      text: "Know your numbers and approach your next purchase with confidence."
    }
  ];

  return (
    <>
      <SEO {...seoConfig} />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#fffdfa] pt-40 pb-10">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mx-auto max-w-[1200px] px-6 lg:px-16"
        >
          <motion.div variants={fadeUp} className="flex items-center gap-5 mb-8">
            <div className="h-px w-16 bg-[#8b8172]" />
            <span className="text-xs uppercase tracking-[0.45em] text-[#6d655b]">
              Financial Tools
            </span>
          </motion.div>

          <motion.h1
            variants={fadeUp}
            className="font-serif text-5xl leading-[1.05] text-[#2b2925] lg:text-7xl"
          >
            Mortgage
            <br />
            Calculator
          </motion.h1>

          <motion.p
            variants={fadeUp}
            className="mt-10 max-w-3xl text-lg leading-9 text-[#5f594f]"
          >
            Estimate your monthly mortgage payments, explore your financing
            options, and understand what fits comfortably within your budget
            before making your next move.
          </motion.p>
        </motion.div>
      </section>

      {/* FEATURES */}
      <section className="relative bg-[#fffdfa] pb-20">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mx-auto max-w-[1200px] px-6 lg:px-16"
        >
          <div className="grid gap-10 md:grid-cols-3">
            {
              cards.map((card, index) => (
                <motion.div
                  key={index}
                  variants={fadeUp}
                  whileHover={{ y: -6 }}
                  className="border-[15px] border-[#d3d2d0]/70 p-5"
                >
                  <div className="flex h-14 w-14 items-center justify-center border border-[#8b8172]">
                    <FontAwesomeIcon
                      icon={card.icon}
                      className="text-xl text-[#8b8172]"
                    />
                  </div>

                  <h3 className="mt-8 font-serif text-3xl text-[#2b2925]">
                    {card.title}
                  </h3>

                  <p className="mt-5 leading-8 text-[#5f594f]">
                    {card.text}
                  </p>

                </motion.div>
              ))
            }
          </div>
        </motion.div>
      </section>

      {/* CALCULATOR */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="bg-[#fffdfa] pb-24"
      >
        <div className="mx-auto max-w-[1200px] px-6 lg:px-16">
          <div className="border border-[#d8d0c6] bg-white">
            {/* Header */}
            <div className="flex flex-col gap-6 border-b border-[#d8d0c6] px-8 py-10 md:flex-row md:items-center md:justify-between lg:px-12">
              <div>
                <p className="text-xs uppercase tracking-[0.4em] text-[#6d655b]">
                  Home Financing
                </p>
                <h2 className="mt-5 font-serif text-4xl text-[#2b2925] lg:text-5xl">
                  Mortgage Payment Calculator
                </h2>
                <p className="mt-5 max-w-2xl leading-8 text-[#5f594f]">
                  Adjust your home price, down payment, interest rate, and loan
                  term to get a clearer picture of your monthly costs.
                </p>
              </div>

              <div className="flex h-16 w-16 items-center justify-center border border-[#8b8172]">
                <FontAwesomeIcon
                  icon={faChartLine}
                  className="text-2xl text-[#8b8172]"
                />
              </div>
            </div>

            {/* Calculator */}
            <div className="bg-white">
              <iframe
                src="https://www.mortgagecalculator.org/webmasters/?downpayment=80000&homevalue=400000&loanamount=320000&interestrate=7&loanterm=30&propertytax=2700&pmi=1&homeinsurance=1500&monthlyhoa=0"
                className="h-[2400px] w-full border-0 md:h-[1200px]"
                scrolling="no"
                title="Mortgage Calculator"
              />
            </div>
          </div>
        </div>
      </motion.section>
    </>
  );
}